/**
 * window.ValerydCards — the storefront's catalogue seam for JS: turning a query or a
 * list of Valeryd article numbers (== Shopify SKUs) into products or product cards.
 *
 *   suggest(query, { signal, limit }) → Promise<product[]>
 *     The predictive-search endpoint's products for a typeahead query, in ONE order:
 *     an exact variant-SKU match first, an exact handle match second, the provider's
 *     own order after that. Rejects on a non-2xx so the caller can show its error state.
 *
 *   cards(skus, { signal }) → Promise<{ bySku: Map<SKU, HTMLLIElement>, incomplete: boolean }>
 *     Server-rendered product cards for the given SKUs, keyed by upper-cased SKU. The
 *     markup is snippets/product-card.liquid, rendered by sections/product-card-grid.liquid
 *     through the Section Rendering API on the search page — so a client-rendered grid
 *     is byte-for-byte the server-rendered one and the card has a single implementation.
 *     A SKU with no live product is simply absent (discontinued/removed SKUs never
 *     render as a dead link or a bare number). `incomplete` is true when at least one
 *     batch failed, so a caller can tell "nothing matched" from "the lookup broke".
 *
 *   searchUrl(skus, query) → string | null
 *     The store's own results page for a list of SKUs, with its filters, sort and paging.
 *     The shopper's query rides first as one more SKU term, so the page can name what was
 *     searched instead of the list (snippets/search-terms.liquid is the one reader). As a SKU
 *     it matches only a product whose article number it is, which is then a right answer.
 *     null for an empty list, or one past the store's 250-product ceiling.
 *
 *   atLevel(li, level) → li
 *     Moves a rendered card's title to `level`, and hands the card back. The card's MARKUP
 *     stays snippets/product-card.liquid's; its heading LEVEL cannot be, because the grid is
 *     rendered once without knowing how deep in the page's outline the caller will put it —
 *     the same card sits under an h1 on a category page and under a category heading on a
 *     fitment result, a search-type heading above that one when a query matched several.
 *     Left at the h3 the grid renders, a card title either
 *     skips a level or ties with the heading above it.
 *
 *   money(price, locale, currency) → string
 *
 *   net(price) → number | null
 *     The price excl. VAT, in the same unit: divided by the VAT the market's prices include, which
 *     layout/theme.liquid marks as <html data-vat-rate> from snippets/vat-rate.liquid, and
 *     rounded to the cent, as snippets/price.liquid does on the server. null when the root
 *     carries no rate — prices without VAT, or a country without a row — and the caller then
 *     prints no ex-VAT line.
 *
 * No SOAP, credentials or ranking beyond the exact-match tie-break live here — search
 * relevance is the platform's, fitment is the middleware's.
 */
(function () {
  if (window.ValerydCards) return;

  // The store's root for the shopper's market and language: '/' on the default, '/de-de/' under the
  // German subfolder. A root-relative '/search' would drop the subfolder and answer in English.
  var ROOT = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/');
  var SUGGEST_URL = ROOT + 'search/suggest.json';
  var CARDS_SECTION = 'product-card-grid';
  // Measured against the dev store 2026-09-05: a search of quoted SKUs joined with OR answers
  // every one of them up to eight terms in every shape tried; at ten terms the same query
  // returns NOTHING for some sets (alphanumeric SKUs seem to count double), and at eleven
  // for all. Eight is the ceiling with the evidence behind it, so 267 fitment articles cost
  // 34 renders instead of 267 lookups.
  //
  // By 2026-09-29 the store had stopped matching a bare quoted SKU at all ("4010006A" finds
  // nothing; 3 of a brake ID's 37 parts rendered), so each SKU is now a `variants.sku` field
  // term. In that form 3 791 of 3 794 live dev SKUs resolved in batches of eight — the other
  // three are missing from the search index under every form — and 37 resolved in one request.
  var CARDS_BATCH = 8;
  // The store answers at most 250 products per search: 251 SKUs came back as 250 (measured
  // 2026-09-29, dev). A longer list would lose parts on the results page.
  var SEARCH_MAX = 250;

  function normaliseSku(sku) {
    return String(sku || '')
      .trim()
      .toUpperCase();
  }

  // Normalised and de-duplicated, first occurrence wins.
  function uniqueSkus(skus) {
    var wanted = [];
    var seen = {};
    (skus || []).forEach(function (sku) {
      var key = normaliseSku(sku);
      if (key && !seen[key]) {
        seen[key] = true;
        wanted.push(key);
      }
    });
    return wanted;
  }

  // Quoted so a hyphenated article number (9-50103) stays one value.
  function skuTerm(sku) {
    return 'variants.sku:"' + String(sku).replace(/"/g, '') + '"';
  }

  function handleOf(product) {
    var handle = ((product && product.handle) || '').toLowerCase();
    if (!handle && product && product.url) {
      handle = product.url.split('?')[0].split('/').filter(Boolean).pop().toLowerCase();
    }
    return handle;
  }

  // Exact variant SKU beats exact handle beats the provider's order. The handle form
  // of a SKU is what Shopify makes of it in a URL: lower-case, non-alphanumerics → '-'.
  function rank(products, query) {
    var exactSku = normaliseSku(query);
    var handleSku = exactSku
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return products
      .map(function (product, index) {
        var variants = Array.isArray(product.variants) ? product.variants : [];
        var exactVariant = variants.some(function (variant) {
          return normaliseSku(variant && variant.sku) === exactSku;
        });
        var handle = handleOf(product);
        var exactHandle = !!handleSku && (handle === handleSku || handle.endsWith('-' + handleSku));
        return { index: index, product: product, score: exactVariant ? 2 : exactHandle ? 1 : 0 };
      })
      .sort(function (a, b) {
        return b.score - a.score || a.index - b.index;
      })
      .map(function (entry) {
        return entry.product;
      });
  }

  async function suggest(query, opts) {
    opts = opts || {};
    var url =
      SUGGEST_URL +
      '?q=' +
      encodeURIComponent(query) +
      '&resources[type]=product&resources[limit]=' +
      (opts.limit || 8) +
      '&resources[options][unavailable_products]=last' +
      '&resources[options][fields]=title,product_type,variants.title,variants.sku,vendor';
    var res = await fetch(url, { headers: { Accept: 'application/json' }, signal: opts.signal });
    if (!res.ok) throw new Error('Predictive search failed with status ' + res.status);
    var data = await res.json();
    var products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
    return rank(products, query);
  }

  function searchUrl(skus, query) {
    var wanted = uniqueSkus(skus);
    if (!wanted.length || wanted.length > SEARCH_MAX) return null;
    var term = String(query || '').trim();
    var terms = (term ? [term] : []).concat(wanted);
    return ROOT + 'search?type=product&q=' + encodeURIComponent(terms.map(skuTerm).join(' OR '));
  }

  async function renderBatch(skus, signal) {
    var q = skus.map(skuTerm).join(' OR ');
    var url = ROOT + 'search?section_id=' + CARDS_SECTION + '&type=product&q=' + encodeURIComponent(q);
    var res = await fetch(url, { headers: { Accept: 'text/html' }, signal: signal });
    if (!res.ok) throw new Error('Card render failed with status ' + res.status);
    var doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    return Array.prototype.slice.call(doc.querySelectorAll('[data-card-grid] > li[data-skus]'));
  }

  async function cards(skus, opts) {
    opts = opts || {};
    var wanted = uniqueSkus(skus);
    var seen = {};
    wanted.forEach(function (key) {
      seen[key] = true;
    });
    var bySku = new Map();
    if (!wanted.length) return { bySku: bySku, incomplete: false };

    var batches = [];
    for (var i = 0; i < wanted.length; i += CARDS_BATCH) batches.push(wanted.slice(i, i + CARDS_BATCH));
    var settled = await Promise.allSettled(
      batches.map(function (batch) {
        return renderBatch(batch, opts.signal);
      })
    );
    var incomplete = false;
    settled.forEach(function (result) {
      if (result.status !== 'fulfilled') {
        incomplete = true;
        return;
      }
      result.value.forEach(function (li) {
        (li.getAttribute('data-skus') || '').split(',').forEach(function (raw) {
          var key = normaliseSku(raw);
          if (key && seen[key] && !bySku.has(key)) bySku.set(key, li);
        });
      });
    });
    return { bySku: bySku, incomplete: incomplete };
  }

  function money(price, locale, currency) {
    var amount = parseFloat(price);
    if (isNaN(amount)) return '';
    try {
      return new Intl.NumberFormat(locale || 'en', { style: 'currency', currency: currency || 'EUR' }).format(amount);
    } catch (error) {
      return amount.toFixed(2);
    }
  }


  function net(price) {
    var rate = parseFloat(document.documentElement.getAttribute('data-vat-rate'));
    var amount = parseFloat(price);
    if (isNaN(rate) || isNaN(amount)) return null;
    return Math.round((amount * 10000) / (100 + rate)) / 100;
  }

  function atLevel(li, level) {
    var title = li.querySelector('.product-card__title');
    var tag = 'H' + Math.min(Math.max(level, 1), 6);
    if (!title || title.tagName === tag) return li;
    var replacement = document.createElement(tag);
    title.getAttributeNames().forEach(function (name) {
      replacement.setAttribute(name, title.getAttribute(name));
    });
    replacement.replaceChildren.apply(replacement, Array.from(title.childNodes));
    title.replaceWith(replacement);
    return li;
  }

  window.ValerydCards = { suggest: suggest, cards: cards, searchUrl: searchUrl, atLevel: atLevel, money: money, net: net };
})();
